// @ts-strict-ignore
import type { useSnackBar } from '@usealloy/component-library';

import { getNumberChildActions, getActionName } from 'flow/service/actions';
import {
  getAllRelevantSupplementalData, getSelectedActionAndRelevantData,
} from 'flow/editor/components/MagicImport/helpers';
import {
  removeAction,
  resetNodeAndConnectorState,
  setFocusedNodeId,
  setIsTagSwapMode,
} from 'flow/store/redux/slice';
import type { WorkflowNode } from 'flow/service/graph';
import type { WorkflowState } from 'flow/store/redux/state';

import type { AppDispatch } from '~/store';
import { captureExceptionsToSentry } from '~/helpers/exceptionHandler';


export function defaultOnCopyAction({
  nodeId,
  workflowData,
  workflowProps,
  addSnackBar,
}: {
  nodeId: WorkflowNode['id'];
  workflowData: WorkflowState['workflowData'];
  workflowProps: WorkflowState['workflowProps'];
  addSnackBar: ReturnType<typeof useSnackBar>;
}) {
  return function handler() {
    if (nodeId) {
      try {
        const clipboardData = getSelectedActionAndRelevantData({
          selectedNode: nodeId,
          workflowData,
        });
        const { services, reasonCodes, reasonCodeGroups } = workflowProps;
        const supplementalData = getAllRelevantSupplementalData({
          actions: clipboardData.actions,
          thresholds: clipboardData.thresholds,
          dataRoutes: clipboardData.dataRoutes,
          services,
          reasonCodes,
          reasonCodeGroups,
        });

        navigator.clipboard.writeText(JSON.stringify({
          ...clipboardData,
          serviceAttributeData: supplementalData.serviceAttributes,
          reasonCodeGroupData: supplementalData.reasonCodeGroups,
        }));

        const targetAction = Object.entries(clipboardData.actions)[0][1];

        addSnackBar({
          duration: 2000,
          title: `Copied ${getActionName(targetAction)} and the relevant data to the clipboard.`,
          type: 'success',
        });
      } catch (error) {
        captureExceptionsToSentry(error);
        addSnackBar({
          duration: 2000,
          title: 'Something went wrong while copying the tag.',
          type: 'danger',
        });
      }
    }
  };
}

export function removeActionAndResetNodeState({
  addSnackBar,
  dispatch,
  nodeId,
}: {
  addSnackBar: ReturnType<typeof useSnackBar>;
  dispatch: AppDispatch;
  nodeId: WorkflowNode['id'];
}) {
  dispatch(removeAction({
    id: nodeId,
  }));

  addSnackBar({
    duration: 2000,
    title: 'Deleted the action.',
    type: 'success',
  });
  setTimeout(() => dispatch(resetNodeAndConnectorState()), 10);
}

export function defaultOnDeleteAction({
  addSnackBar,
  dispatch,
  nodeId,
  workflowData,
  setNodeDeletionChildActionCounts,
}: {
  addSnackBar: ReturnType<typeof useSnackBar>;
  dispatch: AppDispatch;
  nodeId: WorkflowNode['id'];
  workflowData: WorkflowState['workflowData'];
  setNodeDeletionChildActionCounts: React.Dispatch<React.SetStateAction<number>>;
}) {
  return function handler() {
    if (nodeId) {
      const childActionCounts = getNumberChildActions({
        actionPublicId: nodeId,
        workflowData,
      });

      if (childActionCounts > 0) {
        setNodeDeletionChildActionCounts(childActionCounts);
      } else {
        removeActionAndResetNodeState({
          addSnackBar,
          dispatch,
          nodeId,
        });
      }
    }
  };
}

export function defaultOnSwapAction({
  dispatch,
  nodeId,
}: {
  dispatch: AppDispatch;
  nodeId: WorkflowNode['id'];
}) {
  return function handler() {
    dispatch(setFocusedNodeId(nodeId));
    dispatch(setIsTagSwapMode(true));
  };
}

export function defaultOnUpdateAction({
  onUpdateAction,
}: {
  onUpdateAction: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  return function handler() {
    onUpdateAction(true);
  };
}
